/**
 * Decodes a JWT's payload without verifying its signature.
 *
 * Verification is deliberately NOT done here and must never be added: the
 * browser cannot hold a trustworthy key, and every authorization decision this
 * value feeds is re-made server-side against a JWKS-validated token. This
 * exists only so the UI can render what the user is already entitled to --
 * showing or hiding a nav item, not granting access.
 *
 * Returns null on anything malformed rather than throwing, so a bad or absent
 * token degrades to "no claims" instead of breaking app start-up.
 */
export const decodeJwtPayload = (
  token: string | null | undefined
): Record<string, unknown> | null => {
  if (!token) {
    return null;
  }

  const segments = token.split(".");
  if (segments.length < 2) {
    return null;
  }

  const payload = segments[1];
  if (!payload) {
    return null;
  }

  try {
    // JWTs use base64url; atob needs base64, and the padding is stripped.
    const base64 = payload.replace(/-/g, "+")
.replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );
    const json = decodeURIComponent(
      atob(padded)
.split("")
.map((c) => `%${`00${c.charCodeAt(0)
.toString(16)}`.slice(-2)}`)
.join("")
    );
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
};
